import { useContext, useEffect, useState } from 'react';
import { B } from '../constants/brand';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { UserContext } from '../context/UserContext';
import { Card, Spinner, ErrorMsg, useToast, EmptyState, Btn, Icon } from '../components/ui';
import { TaskRow, QuickNoteComposer, completeRefreshDelay } from '../components/tasks/TaskComponents';
import { TaskDetailModal } from '../components/shared/TaskDetailModal';
import { CapitalDeploymentWidget } from '../components/dashboard/CapitalDeploymentWidget';
import { MorningBriefWidget } from '../components/dashboard/MorningBriefWidget';
import { QuickNoteWidget } from '../components/dashboard/QuickNoteWidget';
import { DashboardCustomizeModal } from '../components/dashboard/DashboardCustomizeModal';
import {
  WIDGET_BY_ID, normalizeLayout, visibleWidgets, defaultLayout,
  toLayoutBody, layoutsEqual,
} from '../constants/dashboardWidgets';
import { formatInstant } from '../utils/format';

function greeting(firstName) {
  const h = new Date().getHours();
  const tod = h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
  return `Good ${tod}, ${firstName}`;
}

// ============================================================
// Dashboard — a per-user arrangement of independent widgets.
//
// The widget catalog lives in constants/dashboardWidgets.js; the user's chosen
// order / visibility / width is stored on their account
// (GET/PUT /api/dashboard/layout → dbo.UserDashboardLayouts, sql/146) so the
// same layout follows them to any browser. A user who has never customized
// anything gets the catalog's default order, which is the dashboard as it was
// before this became configurable.
//
// The greeting header is NOT a widget — it always leads.
// ============================================================
export const Dashboard = ({ setPage, onTaskClick, onOpenNote }) => { // eslint-disable-line no-unused-vars
  const user = useContext(UserContext);
  const isAdmin = user?.role === 'Admin';
  const { toast } = useToast();
  // "View as of" — '' is the live dashboard. A past day shows the tasks that
  // were open then; a future day shows today's tasks with overdue counted
  // against that day. The API returns caveats saying what is reconstructed.
  const [asOf, setAsOf] = useState('');
  const { data, loading, error, reload } = useApi(
    () => api.get(asOf ? `dashboard?asOf=${encodeURIComponent(asOf)}` : 'dashboard'), [asOf]);
  const { data: users } = useApi(() => api.get('users'), []);

  // ---- Layout preferences -------------------------------------------------
  // Loaded once; edits are applied optimistically (the modal already validated
  // the shape) and persisted with a PUT. A failed load or a pre-migration API
  // silently falls back to the default layout — the dashboard must never be
  // blocked by a preference lookup.
  const { data: savedLayout, error: layoutError } = useApi(() => api.get('dashboard/layout'), []);
  const [layout, setLayout]       = useState(null);   // null = not resolved yet
  const [customizing, setCustomizing] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);

  useEffect(() => {
    if (savedLayout !== null && savedLayout !== undefined) setLayout(normalizeLayout(savedLayout.layout));
    else if (layoutError) setLayout(defaultLayout());
  }, [savedLayout, layoutError]);

  const saveLayout = async (next) => {
    // A no-op edit shouldn't hit the API.
    if (layout && layoutsEqual(next, layout)) { setCustomizing(false); return; }
    setSavingLayout(true);
    const previous = layout;
    setLayout(next);                                   // optimistic
    try {
      // "Reset to default" in the modal produces exactly the default layout —
      // drop the stored row instead of persisting a copy of the defaults, so
      // the user keeps tracking the catalog as new widgets ship.
      if (layoutsEqual(next, defaultLayout())) await api.del('dashboard/layout');
      else await api.put('dashboard/layout', toLayoutBody(next));
      toast.success('Dashboard layout saved');
      setCustomizing(false);
    } catch (e) {
      setLayout(previous);                             // roll back
      toast.error(e.message || 'Could not save your layout');
    } finally { setSavingLayout(false); }
  };

  const [selectedTaskId, setSelectedTaskId] = useState(null);  // open in the shared detail modal
  const [quickNoteFor, setQuickNoteFor] = useState(null);
  // Which task list the outstanding-tasks card shows: 'mine' = assigned TO me,
  // 'delegated' = tasks I've assigned OUT to other people. Flipped via the
  // segmented toggle in the card header.
  const [taskView, setTaskView] = useState('mine');

  const openQuickNote = (task) => setQuickNoteFor(task.taskID);

  const changeStatus = async (task, status) => {
    try {
      await api.put(`tasks/${task.taskID}/status`, { status });
      await completeRefreshDelay(status);
      reload();
      return true;
    } catch (e) { toast.error(e.message); return false; }
  };

  // Open a task in the same full detail modal the Tasks table uses — in place
  // on the Dashboard, no navigation.
  const openTask = (task) => setSelectedTaskId(task.taskID);

  // The lists are FLAT. A parent task is a group heading, not work
  // (shared/src/taskTree.ts), and the API leaves groups out of these lists —
  // each subtask is its own row, labelled "↳ parent" by showParent.

  if (loading && !data) return <Spinner />;
  if (error) return (
    <div>
      <ErrorMsg msg={error} onRetry={reload} />
      {asOf && <div style={{ marginTop: 12 }}><Btn size="sm" variant="ghost" onClick={() => setAsOf('')}>Back to today</Btn></div>}
    </div>
  );

  const myTasks          = data?.myTasks ?? [];
  const assignedByMeTasks = data?.assignedByMeTasks ?? [];
  // The list the card is currently showing, driven by the toggle.
  const activeTasks = taskView === 'mine' ? myTasks : assignedByMeTasks;
  const isDelegated = taskView === 'delegated';
  // Derive the counts from the rows actually shown so the header can never
  // disagree with the list (the counts previously stuck at 0 while tasks were
  // listed). Compare local calendar dates — matches fmt.date's slice(0, 10).
  const d = new Date();
  const realToday = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const viewingAsOf = data?.asOf || null;            // the server's answer, not the input box
  const todayStr = viewingAsOf || realToday;
  const caveats = data?.caveats || [];
  const openCount    = activeTasks.length;
  const overdueCount = activeTasks.filter(t => t.dueDate && String(t.dueDate).slice(0, 10) < todayStr).length;

  const firstName = user?.name?.split(' ')[0] ?? user?.name ?? user?.displayName?.split(' ')[0] ?? 'there';

  const renderRows = (list) => list.map(task => (
    <div key={task.taskID}>
      <TaskRow task={task} expanded={false} onToggle={() => {}}
        onOpen={openTask} onAddChild={null}
        onStatusChange={changeStatus} onQuickNote={openQuickNote} showDeal showParent />
      {quickNoteFor === task.taskID && (
        <QuickNoteComposer taskId={task.taskID} taskTitle={task.title} onClose={() => setQuickNoteFor(null)} />
      )}
    </div>
  ));

  // ---- Widgets ------------------------------------------------------------
  // One branch per catalog id. A catalog entry with no branch here renders
  // nothing (rather than crashing) — the two are meant to be kept in step.
  const renderWidget = (id) => {
    switch (id) {
      case 'outstandingTasks':
        // Outstanding tasks — one card, toggled between "assigned to me" and
        // "assigned by me to others" via the segmented switch in the header.
        return (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '14px 16px', borderBottom: `1px solid ${B.border}` }}>
              {/* Segmented toggle: pick which set of tasks to view */}
              <div role="tablist" aria-label="Task view"
                style={{ display: 'inline-flex', background: B.surfaceSunken, border: `1px solid ${B.border}`, borderRadius: 20, padding: 3 }}>
                {[
                  { key: 'mine',      label: 'Assigned to me',    count: myTasks.length },
                  { key: 'delegated', label: "I've assigned out", count: assignedByMeTasks.length },
                ].map(seg => {
                  const active = taskView === seg.key;
                  return (
                    <button key={seg.key} role="tab" aria-selected={active}
                      onClick={() => setTaskView(seg.key)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '5px 14px', borderRadius: 18, border: 'none',
                        background: active ? B.navy : 'transparent',
                        color: active ? B.white : B.textMd,
                        fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
                        transition: 'background 0.15s ease, color 0.15s ease',
                      }}>
                      {seg.label}
                      <span style={{
                        fontSize: 11, fontWeight: 700, lineHeight: 1,
                        padding: '2px 7px', borderRadius: 10,
                        background: active ? 'rgba(255,255,255,0.22)' : B.surfaceMuted,
                        color: active ? B.white : B.textMd,
                      }}>{seg.count}</span>
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: B.textMd }}>{openCount} open</span>
                {overdueCount > 0 && (
                  <span style={{ fontSize: 12, fontWeight: 700, color: B.red, background: B.red + '18', border: `1px solid ${B.red}44`, borderRadius: 20, padding: '2px 10px' }}>
                    {overdueCount} overdue
                  </span>
                )}
              </div>
            </div>

            {activeTasks.length > 0 ? (
              <>
                {/* Column header — mirrors the Tasks table */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: `2px solid ${B.border}`, background: B.surfaceMuted, fontSize: 11, fontWeight: 600, color: B.textMd, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  <div style={{ width: 20 }} />
                  <div style={{ width: 16, marginRight: 10 }} />
                  <div style={{ flex: 1 }}>Task</div>
                  <div style={{ width: 100, textAlign: 'center' }}>Status</div>
                  <div style={{ width: 80,  textAlign: 'center' }}>Priority</div>
                  <div style={{ width: 90,  textAlign: 'right'  }}>Due</div>
                  <div style={{ width: 80,  textAlign: 'right', paddingLeft: 8 }}>{isDelegated ? 'Assigned to' : 'Assignees'}</div>
                </div>
                {renderRows(activeTasks)}
              </>
            ) : (
              isDelegated
                ? <EmptyState icon="info" title="Nothing delegated" message="You haven't assigned any open tasks to other people." />
                : <EmptyState icon="success" title="All clear" message="You have no outstanding tasks." />
            )}
          </Card>
        );

      case 'capitalDeployment':
        return <CapitalDeploymentWidget />;

      case 'quickNote':
        return <QuickNoteWidget onOpenInNotes={onOpenNote} />;

      case 'morningBrief':
        return <MorningBriefWidget users={users || []} />;

      default:
        return null;
    }
  };

  // `email` gates the widgets restricted to named people (see `ownerEmails` in
  // the catalog) — the same identity field the nav's `ownerEmail` items use.
  const shown = visibleWidgets(layout, { isAdmin, email: user?.email });

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Greeting header — always first, not a widget */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 28 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: B.text }}>
            {greeting(firstName)}
          </div>
          <div style={{ fontSize: 14, color: B.textMd, marginTop: 4 }}>
            {formatInstant(viewingAsOf ? new Date(`${viewingAsOf}T12:00:00`) : new Date(), { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: B.textMd, fontWeight: 600 }}>
            View as of
            <input type="date" value={asOf || realToday}
              onChange={e => setAsOf(e.target.value === realToday ? '' : e.target.value)}
              aria-label="View the dashboard as of a date"
              style={{ fontSize: 13, fontFamily: 'inherit', padding: '4px 8px', border: `1px solid ${B.border}`, borderRadius: 8, color: B.text, background: B.surface }} />
          </label>
          {asOf && <Btn variant="ghost" size="sm" onClick={() => setAsOf('')}>Today</Btn>}
          <Btn variant="ghost" size="sm" onClick={() => setCustomizing(true)} disabled={!layout}>
            <Icon name="settings" size={14} /> Customize
          </Btn>
        </div>
      </div>

      {viewingAsOf && (
        <div role="status" style={{ marginBottom: 20, padding: '10px 14px', borderRadius: 10, background: B.amber + '18', border: `1px solid ${B.amber}55`, fontSize: 13, color: B.text }}>
          <div style={{ fontWeight: 700, marginBottom: caveats.length ? 4 : 0 }}>
            Viewing the dashboard as of {formatInstant(new Date(`${viewingAsOf}T12:00:00`), { month: 'long', day: 'numeric', year: 'numeric' })}
            {loading ? ' — loading…' : ''}
          </div>
          {caveats.map((c, i) => <div key={i} style={{ color: B.textMd, lineHeight: 1.45 }}>{c}</div>)}
        </div>
      )}

      {/* Widgets, in the user's order. `half`-width widgets pair up on a wide
          viewport and fall back to full width when there isn't room. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'flex-start' }}>
        {shown.map(entry => (
          <div key={entry.id}
            style={{
              flex: entry.width === 'half' ? '1 1 calc(50% - 14px)' : '1 1 100%',
              minWidth: entry.width === 'half' ? 320 : 0,
              // `min-width: 0` lets a full-width flex child shrink instead of
              // being forced wide by its own table content.
            }}>
            {renderWidget(entry.id)}
          </div>
        ))}
      </div>

      {layout && shown.length === 0 && (
        <Card>
          <EmptyState icon="info" title="Your dashboard is empty"
            message={`Every widget is hidden. Use "Customize" to bring ${WIDGET_BY_ID.outstandingTasks.title} and the others back.`}
            action={<Btn size="sm" onClick={() => setCustomizing(true)}>Customize</Btn>} />
        </Card>
      )}

      {customizing && layout && (
        <DashboardCustomizeModal
          layout={layout} isAdmin={isAdmin} email={user?.email} saving={savingLayout}
          onClose={() => setCustomizing(false)} onSave={saveLayout} />
      )}

      {selectedTaskId && (
        <TaskDetailModal
          taskId={selectedTaskId}
          users={users || []}
          onClose={() => setSelectedTaskId(null)}
          onChanged={() => { reload(); }}
        />
      )}
    </div>
  );
};
